import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronDown, Filter, Layers } from 'lucide-react';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { DashboardToggle, type DashboardView } from '@/components/layout/DashboardToggle';
import { FinancialDashboard } from '@/components/financial/FinancialDashboard';
import { LogisticsDashboard } from '@/components/logistics/LogisticsDashboard';
import { useChannels } from '@/hooks/useChannels';
import { useActiveChannel } from '@/store/activeChannel';
import { cn } from '@/lib/utils';

// ─── Period picker ────────────────────────────────────────────────────────────

type PeriodKey = 'current_month' | 'last_month' | 'quarter' | 'year';

interface DateRange { from: Date; to: Date }

const PERIOD_LABELS: Record<PeriodKey, string> = {
  current_month: 'Mês corrente',
  last_month:    'Mês anterior',
  quarter:       'Trimestre atual',
  year:          'Ano atual',
};

function getPeriodRange(key: PeriodKey): DateRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case 'current_month': return { from: new Date(y, m, 1),               to: now };
    case 'last_month':    return { from: new Date(y, m - 1, 1),           to: new Date(y, m, 0, 23, 59, 59) };
    case 'quarter':       return { from: new Date(y, Math.floor(m / 3) * 3, 1), to: now };
    case 'year':          return { from: new Date(y, 0, 1),               to: now };
  }
}

function PeriodPicker({ period, onChange }: { period: PeriodKey; onChange: (p: PeriodKey) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} className="btn-secondary !py-1.5 gap-2">
        <Calendar className="w-4 h-4" />
        {PERIOD_LABELS[period]}
        <ChevronDown className="w-3.5 h-3.5 text-ink-muted" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-44 card p-1.5 z-30 shadow-xl">
          {(Object.keys(PERIOD_LABELS) as PeriodKey[]).map((k) => (
            <button
              key={k}
              onClick={() => { onChange(k); setOpen(false); }}
              className={cn(
                'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                period === k ? 'bg-brand-50 text-brand-800 font-semibold' : 'hover:bg-surface-subtle text-ink'
              )}
            >
              {PERIOD_LABELS[k]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Filtros dropdown ─────────────────────────────────────────────────────────

function FiltersDropdown() {
  const { channels } = useChannels();
  const { activeChannelId, setActiveChannel } = useActiveChannel();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const hasFilter = !!activeChannelId;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn('btn-secondary !py-1.5 gap-2', hasFilter && 'ring-2 ring-brand-400')}
      >
        <Filter className="w-4 h-4" />
        Filtros
        {hasFilter && <span className="w-1.5 h-1.5 rounded-full bg-brand-600 shrink-0" />}
        <ChevronDown className="w-3.5 h-3.5 text-ink-muted" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 card p-3 z-30 shadow-xl space-y-3">
          <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">Canal</p>
          <div className="space-y-1">
            <button
              onClick={() => { setActiveChannel(null); setOpen(false); }}
              className={cn(
                'w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors',
                !activeChannelId ? 'bg-brand-50 text-brand-800 font-semibold' : 'hover:bg-surface-subtle text-ink'
              )}
            >
              <Layers className="w-4 h-4 shrink-0" />
              Geral (todos)
            </button>
            {channels.map((c) => (
              <button
                key={c.id}
                onClick={() => { setActiveChannel(c.id); setOpen(false); }}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors',
                  activeChannelId === c.id ? 'bg-brand-50 text-brand-800 font-semibold' : 'hover:bg-surface-subtle text-ink'
                )}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard page ───────────────────────────────────────────────────────────

interface Props {
  initialView: DashboardView;
}

export function Dashboard({ initialView }: Props) {
  const [view, setView]     = useState<DashboardView>(initialView);
  const [period, setPeriod] = useState<PeriodKey>('current_month');
  const navigate = useNavigate();
  const dateRange = getPeriodRange(period);

  const handleChange = (v: DashboardView) => {
    setView(v);
    navigate(v === 'financial' ? '/financeiro' : '/logistica', { replace: true });
  };

  return (
    <div className="h-full flex bg-surface">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />

        <main className="flex-1 overflow-y-auto">
          <div className="px-4 lg:px-8 py-6 max-w-[1600px] mx-auto">
            {/* Top strip: title + toggle + filters */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <p className="stat-label">
                  {view === 'financial' ? 'BPO Financeiro' : 'Gestão de E-commerce'}
                </p>
                <h1 className="font-display font-bold text-2xl text-ink mt-1">
                  {view === 'financial' ? 'Visão financeira consolidada' : 'Mercado Livre — operação e vendas'}
                </h1>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {view === 'financial' && (
                  <>
                    <PeriodPicker period={period} onChange={setPeriod} />
                    <FiltersDropdown />
                  </>
                )}
                <DashboardToggle value={view} onChange={handleChange} />
              </div>
            </div>

            {view === 'financial' ? <FinancialDashboard dateRange={dateRange} /> : <LogisticsDashboard />}
          </div>
        </main>
      </div>
    </div>
  );
}
